import { useState, useEffect } from 'react';
import { Users, Copy, Check, Twitter, Facebook, Mail, Share2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { getUserReferralStats } from '@/lib/referralUtils';
import { shareToTwitter, shareToFacebook, shareViaEmail, shareNative } from '@/lib/shareUtils';

interface InviteFriendsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function InviteFriendsDialog({ isOpen, onClose }: InviteFriendsDialogProps) {
  const { user } = useAuth();
  const [referralLink, setReferralLink] = useState<string | null>(null);
  const [referralCount, setReferralCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && user) {
      loadReferralData();
    }
  }, [isOpen, user]);

  const loadReferralData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const stats = await getUserReferralStats(user.id);
      setReferralLink(stats.referralLink);
      setReferralCount(stats.totalReferrals);
    } catch (error) {
      console.error('Error loading referral data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load referral link. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: 'Link copied!',
        description: 'Your referral link has been copied to clipboard.',
      });
    } catch (error) {
      toast({
        title: 'Failed to copy',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleTwitterShare = () => {
    if (!referralLink) return;
    shareToTwitter({
      text: `Join me on Dino - your playful reading companion! 🦕📚 Track your books, share reviews, and discover amazing stories.`,
      url: referralLink,
      hashtags: ['Dino', 'Reading', 'Books'],
    });
  };

  const handleFacebookShare = () => {
    if (!referralLink) return;
    shareToFacebook(referralLink);
  };

  const handleEmailShare = () => {
    if (!referralLink) return;
    shareViaEmail({
      title: 'Join me on Dino!',
      text: `Hey! I've been using Dino to track my reading and I think you'd love it too. It's a fun way to organize your books, share reviews, and discover new stories. Join me using my link:`,
      url: referralLink,
    });
  };

  const handleNativeShare = async () => {
    if (!referralLink) return;
    await shareNative({
      title: 'Join me on Dino!',
      text: `Join me on Dino - your playful reading companion! 🦕📚`,
      url: referralLink,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Invite Friends
          </DialogTitle>
          <DialogDescription>
            Share Dino with friends and grow the reading community together!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Referral Stats */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">{referralCount}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  {referralCount === 1 ? 'Friend' : 'Friends'} Invited
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Referral Link */}
          <div className="space-y-2">
            <Label htmlFor="referral-link">Your Personal Invite Link</Label>
            <div className="flex gap-2">
              <Input
                id="referral-link"
                value={referralLink || (loading ? 'Loading...' : 'Unavailable')}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                size="icon"
                variant="outline"
                onClick={handleCopy}
                disabled={!referralLink || loading}
              >
                {copied ? (
                  <Check className="w-4 h-4 text-success" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Anyone who signs up using your link will be connected with you on Dino
            </p>
          </div>

          {/* Share Buttons */}
          <div className="space-y-2">
            <Label>Share via</Label>
            <div className="grid grid-cols-2 gap-2">
              {navigator.share && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNativeShare}
                  disabled={!referralLink || loading}
                  className="w-full"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleTwitterShare}
                disabled={!referralLink || loading}
                className="w-full"
              >
                <Twitter className="w-4 h-4 mr-2" />
                X / Twitter
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleFacebookShare}
                disabled={!referralLink || loading}
                className="w-full"
              >
                <Facebook className="w-4 h-4 mr-2" />
                Facebook
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleEmailShare}
                disabled={!referralLink || loading}
                className="w-full"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
